// RFQ files: what may be stored against an RFQ, and how each kind is labelled.
//
// Shared by the server (validation) and the client (the picker refuses the same
// things before a byte is sent). Pure — no I/O.
//
// Files are kept exactly as received. Nothing is compressed or re-encoded: the
// SHA-256 recorded at upload is the audit proof that the 8130-3 on file is the
// one the vendor sent, and any rewrite of the bytes would break it.

/** One file. A guard against accidents (a 4 GB folder zip), not a cost control. */
export const RFQ_DOCUMENT_MAX_BYTES = 500 * 1024 * 1024;

export const RFQ_DOCUMENT_KINDS = [
  'faa_8130_3',
  'easa_form_1',
  'coc',
  'traceability',
  'vendor_quote',
  'customer_rfq',
  'customer_po',
  'invoice',
  'packing_slip',
  'photo',
  'correspondence',
  'other',
] as const;
export type RfqDocumentKind = (typeof RFQ_DOCUMENT_KINDS)[number];

export const RFQ_DOCUMENT_KIND_LABELS: Record<RfqDocumentKind, string> = {
  faa_8130_3: 'FAA 8130-3',
  easa_form_1: 'EASA Form 1',
  coc: 'CoC',
  traceability: 'Traceability',
  vendor_quote: 'Vendor quote',
  customer_rfq: 'Customer RFQ',
  customer_po: 'Customer PO',
  invoice: 'Invoice',
  packing_slip: 'Packing slip',
  photo: 'Photo / video',
  correspondence: 'Correspondence',
  other: 'Other',
};

/** Kinds that travel with the part. These show under the line as its certs. */
export const CERT_KINDS: ReadonlySet<RfqDocumentKind> = new Set(['faa_8130_3', 'easa_form_1', 'coc', 'traceability']);

/**
 * What the bytes must look like, per extension. `text` means no binary
 * signature exists (CSV, a saved .eml): the server instead refuses NUL bytes
 * and markup that a browser would execute.
 */
export type ContentFamily = 'pdf' | 'jpeg' | 'png' | 'webp' | 'tiff' | 'heic' | 'isobmff_video' | 'zip' | 'ole' | 'text';

interface TypeRule {
  family: ContentFamily;
  /** Served with this Content-Type, whatever the uploader's browser claimed. */
  contentType: string;
  /** Opens in the browser tab. Everything else downloads. */
  inline: boolean;
}

export const RFQ_DOCUMENT_TYPES: Record<string, TypeRule> = {
  pdf: { family: 'pdf', contentType: 'application/pdf', inline: true },
  jpg: { family: 'jpeg', contentType: 'image/jpeg', inline: true },
  jpeg: { family: 'jpeg', contentType: 'image/jpeg', inline: true },
  png: { family: 'png', contentType: 'image/png', inline: true },
  webp: { family: 'webp', contentType: 'image/webp', inline: true },
  tif: { family: 'tiff', contentType: 'image/tiff', inline: false },
  tiff: { family: 'tiff', contentType: 'image/tiff', inline: false },
  heic: { family: 'heic', contentType: 'image/heic', inline: false },
  heif: { family: 'heic', contentType: 'image/heif', inline: false },
  mp4: { family: 'isobmff_video', contentType: 'video/mp4', inline: true },
  mov: { family: 'isobmff_video', contentType: 'video/quicktime', inline: false },
  docx: { family: 'zip', contentType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', inline: false },
  xlsx: { family: 'zip', contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', inline: false },
  pptx: { family: 'zip', contentType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation', inline: false },
  zip: { family: 'zip', contentType: 'application/zip', inline: false },
  doc: { family: 'ole', contentType: 'application/msword', inline: false },
  xls: { family: 'ole', contentType: 'application/vnd.ms-excel', inline: false },
  msg: { family: 'ole', contentType: 'application/vnd.ms-outlook', inline: false },
  eml: { family: 'text', contentType: 'message/rfc822', inline: false },
  csv: { family: 'text', contentType: 'text/csv', inline: false },
  txt: { family: 'text', contentType: 'text/plain', inline: false },
};

export const RFQ_DOCUMENT_ACCEPT = Object.keys(RFQ_DOCUMENT_TYPES).map((e) => `.${e}`).join(',');

export function fileExtension(fileName: string): string {
  const base = fileName.split(/[/\\]/).pop() ?? '';
  const dot = base.lastIndexOf('.');
  return dot > 0 ? base.slice(dot + 1).toLowerCase() : '';
}

export function typeRuleFor(fileName: string): TypeRule | null {
  return RFQ_DOCUMENT_TYPES[fileExtension(fileName)] ?? null;
}

/** Why a file would be refused before upload, or null when it is fine. */
export function rfqDocumentRejection(fileName: string, sizeBytes: number): string | null {
  if (!typeRuleFor(fileName)) {
    const ext = fileExtension(fileName);
    return `${ext ? `.${ext}` : 'Files without an extension'} can't be stored. Accepted: PDF, images (JPG, PNG, WEBP, TIFF, HEIC), MP4/MOV video, Word, Excel, PowerPoint, Outlook .msg, .eml, CSV, TXT and ZIP.`;
  }
  if (sizeBytes <= 0) return 'The file is empty.';
  if (sizeBytes > RFQ_DOCUMENT_MAX_BYTES) {
    return `The file is ${(sizeBytes / 1024 / 1024).toFixed(0)} MB; the limit is ${RFQ_DOCUMENT_MAX_BYTES / 1024 / 1024} MB.`;
  }
  return null;
}

/**
 * A first guess at what a file is, from its name. The broker can change it.
 * Order matters: "8130" beats "quote" in "8130-3 for quote 4471.pdf".
 */
export function guessRfqDocumentKind(fileName: string): RfqDocumentKind {
  // Underscores separate words in filenames ("CoC_SN123"), but regex \b treats them as letters.
  const n = fileName.toLowerCase().replace(/_+/g, ' ');
  const ext = fileExtension(fileName);
  if (/8130/.test(n)) return 'faa_8130_3';
  if (/easa|form[\s_-]*1\b|\bf1\b/.test(n)) return 'easa_form_1';
  if (/\bc\s?o\s?c\b|conformi|conformance/.test(n)) return 'coc';
  if (/trace|back[\s_-]*to[\s_-]*birth|\bbtb\b|teardown|non[\s_-]*incident|\bnis\b/.test(n)) return 'traceability';
  if (/packing|pack[\s_-]*slip|packing[\s_-]*list/.test(n)) return 'packing_slip';
  if (/invoice|\binv\b/.test(n)) return 'invoice';
  if (/\bpo\b|purchase[\s_-]*order/.test(n)) return 'customer_po';
  if (/quote|quotation|\bqte\b/.test(n)) return 'vendor_quote';
  if (/\brfq\b|request[\s_-]*for[\s_-]*quot/.test(n)) return 'customer_rfq';
  if (['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif', 'tif', 'tiff', 'mp4', 'mov'].includes(ext)) return 'photo';
  if (['eml', 'msg'].includes(ext)) return 'correspondence';
  return 'other';
}

/** The default cert kind for a line, from what the customer asked for. */
export function certKindForRequested(certRequested: string | null | undefined): RfqDocumentKind {
  if (certRequested === 'easa_form_1') return 'easa_form_1';
  if (certRequested === 'coc') return 'coc';
  return 'faa_8130_3';
}

export type FileGlyph = 'pdf' | 'image' | 'sheet' | 'mail' | 'archive' | 'video' | 'doc' | 'file';

/** Which icon a file gets in the list. Decided by extension, the same signal the type allowlist uses. */
export function fileGlyph(fileName: string): FileGlyph {
  switch (fileExtension(fileName)) {
    case 'pdf': return 'pdf';
    case 'jpg': case 'jpeg': case 'png': case 'webp': case 'tif': case 'tiff': case 'heic': case 'heif': return 'image';
    case 'xlsx': case 'xls': case 'csv': return 'sheet';
    case 'eml': case 'msg': return 'mail';
    case 'zip': return 'archive';
    case 'mp4': case 'mov': return 'video';
    case 'doc': case 'docx': case 'pptx': case 'txt': return 'doc';
    default: return 'file';
  }
}

/** What the collapsed Files header reports: live files only (removed ones are hidden), and how many are certs. */
export function summarizeRfqFiles(docs: { kind: RfqDocumentKind; removedAt: string | null }[]): { total: number; certs: number } {
  const live = docs.filter((d) => !d.removedAt);
  return { total: live.length, certs: live.filter((d) => CERT_KINDS.has(d.kind)).length };
}
